import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, Grid, List, ListItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { fetchCardinality, fetchSimulateEquivalencyRequest, fetchTargetSyllabusActivities, resetCardinality, resetSimulateEquivalency } from '../requestSlice';
import { PlaylistAdd } from '@mui/icons-material';
import './TargetActivitiesTable.css';
import { useTranslation } from 'react-i18next';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { EquivalencyRequestSimulationSearchParams } from '../../../models/EquivalencyRequest';
import { useAppDispatch, useAppSelector } from '../../../shared/redux/store';
import Syllabus, { SuggestedSyllabus } from '../../../models/Syllabus';
import { RequestData, useRequestdata } from '../useRequestData';
import Activity from '../../../models/Activity';
import Institution from '../../../models/Institution';
import { CustomAutocomplete } from '../../../components/Autocomplete/CustomAutocomplete';
import { Proposal, minContentSelected } from '../../../models/Proposal';
import { popSnackbar } from '../../SnackbarProvider/snackbarSlice';
import { Cardinality } from '../../../models/Cardinality';
import ButtonFileUploader from '../../../components/Buttons/ButtonFileUploader';
import { useRuleValidation } from '../../../shared/hooks/useRuleValidation';
import AppFile from '../../../models/AppFile';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { useRequestContext } from '../../Request/Request.Context';
import { useAppContext } from '../../App.Context';
import { useRequestType } from '../../../shared/hooks/useRequestType';
import TargetTableRow from './TargetTableRow/TargetTableRow';

const parseSimulationRequest = (studenId: string, sourceInstitution: Institution, sourceSyllabus: Syllabus, targetSyllabusId: string | undefined, activities: Activity[]): EquivalencyRequestSimulationSearchParams => {
  return {
    student: {
      id: studenId
    },
    syllabus: {
      id: sourceSyllabus.id ? sourceSyllabus.id : null,
      name: sourceSyllabus.name,
      institution: {
        id: sourceInstitution.id ? sourceInstitution.id : null,
        name: sourceInstitution.name
      }
    },
    target_syllabus: {
      id: targetSyllabusId
    },
    activities: activities.map((item) => ({
      id: item?.id,
      activity: {
        id: item.code ? item?.id : null,
        code: item.code,
        name: item.name,
        externalid: item?.externalId
      }
    }))
  };
};

interface TargetActivitiesTableProps {
  sourceInstitution: Institution
  sourceSyllabus: Syllabus,
  targetSyllabus: Syllabus | SuggestedSyllabus | null | undefined,
  activities: Activity[],
  requestData: RequestData
}



const TargetActivitiesTable: React.FC<TargetActivitiesTableProps> = ({ sourceInstitution, sourceSyllabus, targetSyllabus, activities, requestData }) => {
  const {
    proposals,
    setProposals,
    incompleteProposals,
    setIncompleteProposals,
    setAllSourceActivitiesMatchedWithTarget,
    student
  } = useRequestdata({});
  const { user } = useAppContext()
  const { isInternal } = useRequestType();
  const cardinality = useAppSelector(state => state.request.cardinality);
  const targetInstitution = useAppSelector(state => state.request.targetInstitution);
  const targetSyllabusActivities = useAppSelector(state => state.request.targetSyllabusActivities);
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [openUncheckActivityDialog, setOpenUncheckActivityDialog] = useState(false);
  const [activityToUncheck, setActivityToUncheck] = useState<Activity | null>(null);
  const [minContent, setMinContent] = useState<minContentSelected[]>([]);
  const { t } = useTranslation();
  const settings = useAppSelector((state) => state.settings);
  const [isDisabledSourceProposal, setIsDisabledSourceProposal] = useState(false);

  const [selectedSourceProposal, setSelectedSourceProposal] = useState<{ id: string, name: string } | null>(null);
  const [selectedTargetProposal, setSelectedTargetProposal] = useState<{ id: string, name: string, minContent: minContentSelected[] } | null>(null);
  const [isProposalEditionId, setIsProposalEditionId] = useState<string | null>(null);
  const [sourceLabelAutocomplete, setSourceLabelAutocomplete] = useState<string>('');
  const [targetLabelAutocomplete, setTargetLabelAutocomplete] = useState<string>('');

  const programFileSize = useAppSelector(state => state.settings.programFileSize);
  const academictranscriptRule = useRuleValidation(() => !!activities.find(a => a.relationId === selectedSourceProposal?.id)?.program_file);

  const requestContext = useRequestContext();
  const equivalencyMode = (user?.valid && isInternal) ? 'internal' : 'external';



  const { previousRequestFlag } = requestContext;

  const handleClose = () => {
    setOpen(false);
    setIsDisabledSourceProposal(false);
    setSelectedSourceProposal(null);
    setSelectedTargetProposal(null);
    setSourceLabelAutocomplete('');
    setTargetLabelAutocomplete('');
    setMinContent([]);
    setIsProposalEditionId(null);
  };


  const saveIncompleteProposal = (sourceActivityId: string) => {
    const isInProposal = proposals.find((proposal) => proposal.sourceStudentActivityId === sourceActivityId)
    if (isProposalEditionId || isInProposal) return handleClose()
    const proposalData = {
      selectedSourceProposal: selectedSourceProposal,
      selectedTargetProposal: selectedTargetProposal,
      minContent: minContent,
      sourceLabelAutocomplete: sourceLabelAutocomplete,
      targetLabelAutocomplete: targetLabelAutocomplete
    }

    if (incompleteProposals.length === 0) {
      setIncompleteProposals([proposalData]);
    } else {
      if (incompleteProposals.some((incompleteProposal: any) => incompleteProposal.selectedSourceProposal.id === sourceActivityId)) {
        const newIncompleteProposals = incompleteProposals.map((item: any) => {
          if (item.selectedSourceProposal.id === sourceActivityId) {
            return {
              ...proposalData
            }
          }
          return item;
        })
        setIncompleteProposals(newIncompleteProposals);
      } else {
        setIncompleteProposals([...incompleteProposals, proposalData]);
      }
    }
    handleClose()
  }

  const handleAddProposal = () => {
    if (selectedSourceProposal && selectedTargetProposal) {
      let proposalMinContentSelected = null
      let rejectionReason = null
      const minContentThreshold = settings.minContentThreshold
      const minContentSelected = minContent?.map((item: minContentSelected) => ({ id: item.id, description: item.description, status: item.status })) as minContentSelected[]

      const successMesage = () => {
        dispatch(popSnackbar({
          type: 'success',
          message: t('proposalReachesMinThreshold'),
          duration: 5000
        }));
      }
      if (minContentSelected.length > 0) {
        const minContentSelectedCount = minContentSelected.filter((item: minContentSelected) => item.status).length
        const minContentPercentage = (minContentSelectedCount / minContentSelected.length) * 100
        proposalMinContentSelected = minContentSelected.length === 0 ? null : {
          minContentSelected,
          minContentPercentage
        }
        if (minContentPercentage < minContentThreshold) {
          rejectionReason = t('proposalNotReachesMinThreshold')
          dispatch(popSnackbar({
            type: 'warning',
            message: t('proposalNotReachesMinThreshold'),
            duration: 5000
          }));
        } else successMesage()
      } else successMesage()
      const proposal: Proposal = {
        proposalId: selectedSourceProposal.id + selectedTargetProposal.id,
        targetActivityId: selectedTargetProposal.id,
        sourceEquivalencyRequestActivityId: null,
        sourceStudentActivityId: selectedSourceProposal.id,
        state: rejectionReason ? 'REJECTED' : 'PENDING',
        sourceActivityId: activities.find((activity) => activity.relationId === selectedSourceProposal.id)?.id,
        proposalMinContentSelected,
        ...(rejectionReason ? { rejectionReason } : null)
      }
      const isNotInCardinality = !cardinality.some((item) => (item.source_activities.some((sourceActivity: Activity) => (sourceActivity?.id === proposal.sourceActivityId)) && item.target_activities.some((targetActivity: Activity) => (targetActivity?.id === proposal.targetActivityId))))
      const isNotInProposal = !proposals.some((item) => item.proposalId === proposal.proposalId)
      const isEditingTarget = proposal.proposalId !== isProposalEditionId

      if (isNotInCardinality && isNotInProposal && !isProposalEditionId) {
        if (incompleteProposals.some((incompleteProposal: any) => incompleteProposal.selectedSourceProposal.id === selectedSourceProposal.id)) {
          setIncompleteProposals(incompleteProposals.filter((incompleteProposal: any) => incompleteProposal.selectedSourceProposal.id !== selectedSourceProposal.id))
        }
        setProposals([...proposals, proposal]);
        handleClose();
      } else if (isProposalEditionId && !isEditingTarget) {
        setProposals(proposals?.map((item) => {
          if (item.proposalId === isProposalEditionId)
            return { ...proposal, proposalId: isProposalEditionId }
          else return item
        }))
        handleClose();
      } else if (isProposalEditionId && isEditingTarget && isNotInProposal) {
        setProposals(proposals?.map((item) => {
          if (item.proposalId === isProposalEditionId)
            return proposal
          else return item
        }))
        handleClose();
      } else {
        dispatch(popSnackbar({
          type: 'error',
          message: t('proposalDuplicated'),
          duration: 5000
        }));
      }
    }
  }

  const handleTargetProposalSelect = (selected: { id: string, name: string, minContent: minContentSelected[] } | null) => {
    setSelectedTargetProposal(selected ? { ...selected, minContent: selected.minContent ?? [] } : null);
    if (selected?.minContent) setMinContent(selected.minContent.map((item: minContentSelected) => ({ ...item, status: false })));
    else setMinContent([]);
  };

  const handleDeleteProposal = (proposal: Proposal) => {
    const newProposals = requestData.proposals.filter((item) => !(item.sourceStudentActivityId === proposal.sourceStudentActivityId && item.targetActivityId === proposal.targetActivityId));
    const updateActivitiesFile = activities.map((activity) => {
      if (newProposals.some((proposal) => proposal.sourceStudentActivityId === activity.relationId) ||
        incompleteProposals.some((incompleteProposal: any) => incompleteProposal.selectedSourceProposal.id === activity.relationId)) {
        return {
          ...activity
        }
      } else {
        return {
          ...activity,
          program_file: null
        }
      }

    })
    setActivities(updateActivitiesFile)
    setProposals(newProposals);
  }

  const {
    simulation,
    setActivities,
  } = requestData;

  const optionsSourceProposal = activities.map((activity) => ({
    id: activity?.relationId ?? '',
    name: activity.name,
    originalId: activity.id
  })).filter((activity) => !cardinality.some((car) => car.source_activities.some((sourceActivity) => sourceActivity.id === activity.originalId)));

  const optionsTargetProposal = targetSyllabusActivities?.map((activity) => ({
    id: activity.id,
    name: activity.name,
    minContent: activity.minContent ?? []
  }));

  useEffect(() => {
    if (activities.length > 0 && targetSyllabus && previousRequestFlag) {
      const simulateRequest = parseSimulationRequest(
        student?.id ?? '',
        sourceInstitution,
        sourceSyllabus,
        targetSyllabus.id,
        activities
      );
      if (simulation?.syllabus?.id !== sourceSyllabus.id || simulation?.target_syllabus?.id !== targetSyllabus.id || simulation?.activities?.length !== activities?.length) {
        dispatch(fetchSimulateEquivalencyRequest(simulateRequest));
        const targetActivitiesParams = {
          institutionId: targetInstitution?.id ?? '',
          syllabusId: targetSyllabus?.id ?? '',
          hideElectives: true
        };
        const updateProposals = proposals.filter((proposal) => activities.find((activity) => activity.relationId === proposal.sourceStudentActivityId));
        setProposals(updateProposals)
        const updateIncompleteProposals = incompleteProposals.filter((incompleteProposal: any) => activities.find((activity) => activity.relationId === incompleteProposal.selectedSourceProposal.id));
        setIncompleteProposals(updateIncompleteProposals)
        const updateActivitiesFile = activities.map((activity) => {
          if (updateProposals.some((proposal) => proposal.sourceStudentActivityId === activity.relationId)
            || updateIncompleteProposals.some((incompleteProposal: any) => incompleteProposal.selectedSourceProposal.id === activity.relationId)) {
            return {
              ...activity
            }
          } else {
            return {
              ...activity,
              program_file: null
            }
          }
        })
        setActivities(updateActivitiesFile)
        dispatch(fetchTargetSyllabusActivities(targetActivitiesParams))
      }
    } else {
      dispatch(resetSimulateEquivalency());
      setProposals([])
    }
  }, [activities.length, targetSyllabus, previousRequestFlag]);

  useEffect(() => {
    if (simulation && simulation.equivalencies.length > 0) {
      const rulesIds = simulation.equivalencies.map((eq) => eq.rule.id);
      dispatch(fetchCardinality({ ruleIds: Array.from(new Set(rulesIds)) }));
    } else {
      dispatch(resetCardinality());
    }
  }, [simulation])

  const addedActivitiesDisplay: Cardinality[] = activities.filter((activity) => activity.new).map((activity) => ({
    id: activity.relationId,
    source_activities: [activity],
    target_activities: [],
    state: 'WARNING'
  }))

  const activitiesWithOutEquivalency: Cardinality[] = activities.filter((activity) => (!activity.new && !cardinality.some((item: Cardinality) => item.source_activities.some((sourceActivity: Activity) => sourceActivity.id === activity.id)))).map((activity) => ({
    id: activity.relationId,
    source_activities: [activity],
    target_activities: [],
    state: 'WARNING'
  }))

  // Filter out activities without target that are already in the proposals
  const filteredAddedActivitiesDisplay = addedActivitiesDisplay.concat(activitiesWithOutEquivalency).filter(
    (activity) => !proposals.some(
      (proposal) => proposal.sourceStudentActivityId === activity.id
    )
  );

  const proposalsDisplay: Cardinality[] = proposals.map((proposal) => {
    const sourceActivity = activities.find((activity) => activity.relationId === proposal.sourceStudentActivityId);
    const targetActivity = targetSyllabusActivities?.find((activity) => activity.id === proposal.targetActivityId);
    return {
      id: proposal.sourceStudentActivityId,
      source_activities: sourceActivity ? [sourceActivity] : [],
      target_activities: targetActivity ? [targetActivity] : [],
      state: proposal.state,
      proposal
    }
  })

  useEffect(() => {
    if (filteredAddedActivitiesDisplay?.length === 0) {
      setAllSourceActivitiesMatchedWithTarget(true);
    } else {
      setAllSourceActivitiesMatchedWithTarget(false);
    }
  }, [filteredAddedActivitiesDisplay])

  const updateActivities = (attribute: string, value?: AppFile | null) => {
    const activity = activities.find(a => a.relationId === selectedSourceProposal?.id)
    if (!activity) return;
    const equivalencyId = activity.id;
    const activityToUpdate = activities?.find((a: Activity) => a.id === equivalencyId);
    if (activityToUpdate) {
      const activitiesCopy = [...activities];
      const updatedActivity = {
        ...activityToUpdate,
        ruleMatch: activity.ruleMatch,
        [attribute]: value
      };
      const index = activitiesCopy.findIndex((a: Activity) => a.id === equivalencyId);
      if (index !== -1) {
        activitiesCopy[index] = updatedActivity;
        const updatedActivities = [
          ...activitiesCopy.map((a: Activity) => {
            return {
              ...a
            };
          })
        ];
        setActivities(updatedActivities);
      }
    }
  };

  const setFile = (file: AppFile | null) => {
    updateActivities('program_file', file);
  };

  const handleClickOnUncheckActivity = (activity: Activity) => {
    setOpenUncheckActivityDialog(true);
    setActivityToUncheck(activity);
  }

  const handleConfirmUncheckActivity = () => {
    if (activityToUncheck) {
      const updatedActivities = activities.filter((activity) => activity.id !== activityToUncheck.id);
      setActivities(updatedActivities);
      setActivityToUncheck(null);
    }
  }

  const handleCheckMinContent = (id: string, value: boolean) => {
    const updatedMinContent = minContent.map((item: minContentSelected) => {
      if (item.id === id) {
        return {
          ...item,
          status: value
        }
      }
      return item;
    })
    setMinContent(updatedMinContent);
  }
  let tooltipButtonTitle = '';
  if (equivalencyMode === 'internal') {
    tooltipButtonTitle = t('cantAddProposalInternal');
  } else if (optionsSourceProposal.length === 0) {
    tooltipButtonTitle = t('cantAddProposal');
  } else {
    tooltipButtonTitle = t('addProposal');
  }

  const isDisabledAddProposal = !selectedSourceProposal || !selectedTargetProposal || !activities.find(a => a.relationId === selectedSourceProposal?.id)?.program_file || activities.find(a => a.relationId === selectedSourceProposal?.id)?.ruleMatch

  if (!targetSyllabus) return (
    <div className='REXXI-InstructionsContainer'>
      <Grid container display={'flex'} direction={'column'} justifyContent={'center'} gap={'24px'}>
        <Typography align='center'>
          <>
            <TouchAppIcon style={{ fontSize: '5rem' }} color='secondary' />
            <br />
            <br />
            {t('targetSyllabusTitle')}
          </>
        </Typography>
      </Grid>
    </div>
  );

  if (activities?.length === 0) return (
    <div className='REXXI-InstructionsContainer'>
      <Grid container display={'flex'} direction={'column'} justifyContent={'center'} gap={'24px'}>
        <Typography align='center'>
          <>
            <TouchAppIcon style={{ fontSize: '5rem' }} color='secondary' />
            <br />
            <br />
            {t('noActivitiesSelected')}
          </>
        </Typography>
      </Grid>
    </div>
  );

  return (
    <>
      <div className="REQXXI-Request-Accordion" style={{ height: '100%' }}>
        <div className='REQXXI-Block-Container' style={{ height: '100%', borderRadius: '20px' }}>
          {targetSyllabus &&
            <Grid container display={'flex'} direction={'column'} justifyContent={'space-between'} height={'100%'} gap={'24px'}>
              <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body1" color="initial">
                    {t('equivalencyRequestListSimulate')
                    }</Typography>
                </Box>
              </Box>
              <TableContainer sx={{ flex: '1' }}>
                <Table stickyHeader className="REQXXI-EquivalencyRule-Table" size="small" aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <TableCell className="REQXXI-Request-TargetRuleName" align="left">
                        <Typography>
                          <>
                            {sourceInstitution?.name}
                          </>
                        </Typography>
                      </TableCell>
                      <TableCell className="REQXXI-Request-TargetRuleState" align="left">
                        <Typography><>
                          {targetInstitution?.name}</> </Typography>
                      </TableCell>
                      <TableCell className="REQXXI-Request-TargetRuleState" align="center">
                        <Typography><>
                          {t('state')}</> </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className="REQXXI-EquivalencyRule-TableBody" style={{ overflow: 'auto' }}>
                    {cardinality.concat(filteredAddedActivitiesDisplay).concat(proposalsDisplay).map((row: Cardinality, tableRowIndex: number) => {
                      const maxRows = Math.max(row.source_activities.length, row.target_activities.length);
                      
                      return Array.from({ length: maxRows }).map((_, rowIndex) => (
                        <TargetTableRow 
                          key={rowIndex + row.source_activities[rowIndex]?.id + row.target_activities[rowIndex]?.id} 
                          row={row}
                          rowIndex={rowIndex}
                          tableRowIndex={tableRowIndex}
                          proposals={proposals}
                          optionsSourceProposal={optionsSourceProposal}
                          optionsTargetProposal={optionsTargetProposal}
                          incompleteProposals={incompleteProposals}
                          equivalencyMode={equivalencyMode}
                          user={user}
                          handleClickOnUncheckActivity={handleClickOnUncheckActivity}
                          handleDeleteProposal={handleDeleteProposal}
                          setSelectedSourceProposal={setSelectedSourceProposal}
                          setSelectedTargetProposal={setSelectedTargetProposal}
                          setMinContent={setMinContent}
                          setSourceLabelAutocomplete={setSourceLabelAutocomplete}
                          setTargetLabelAutocomplete={setTargetLabelAutocomplete}
                          setIsDisabledSourceProposal={setIsDisabledSourceProposal}
                          setIsProposalEditionId={setIsProposalEditionId}
                          setOpen={setOpen}
                        />
                      ));
                    })}
                  </TableBody>
                </Table>
              </TableContainer >
              <Tooltip placement='top' arrow title={tooltipButtonTitle}>
                <span>
                  <Button
                    data-testid="suggestion-button"
                    variant='outlined'
                    color='primary'
                    onClick={() => setOpen(true)}
                    style={{ marginTop: '20px', width: '100%' }}
                    disabled={optionsSourceProposal.length === 0 || equivalencyMode === 'internal'}
                  >
                    <PlaylistAdd sx={{ m: '0 8px 0 0' }} /> {t('addProposal')}
                  </Button>
                </span>
              </Tooltip>
              {
                equivalencyMode === 'external' && <Typography style={{ fontSize: '12px' }}>{t('proposalInfo')}</Typography>
              }
            </Grid>
          }
        </div>
      </div>
      <Dialog
        open={open}
        onClose={() => selectedSourceProposal ? saveIncompleteProposal(selectedSourceProposal.id) : handleClose()}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth="md"
        PaperProps={{ className: 'REQXXI-MinContent-DialogContent' }}
        className='REQXXI-RequestRow-DialogContainer-rounded'
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description" style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'black' }}>
            <Typography fontSize="20px">{t('proposalDialogTitle')}</Typography>
          </DialogContentText>
          <div style={{ marginTop: '20px' }}>
            <div style={{ width: '100%', marginRight: '10px', display: 'flex', gap: '20px' }}>
              <CustomAutocomplete
                options={optionsSourceProposal}
                optionLabel={(option) => option.name}
                onSelect={setSelectedSourceProposal}
                data-testid='source-activities-autocomplete'
                placeholder={t('proposalSourceActivity')}
                value={selectedSourceProposal}
                inputValue={sourceLabelAutocomplete}
                onInputChange={(value) => setSourceLabelAutocomplete(value)}
                disabled={isDisabledSourceProposal}
              />
              <CustomAutocomplete
                options={optionsTargetProposal}
                optionLabel={(option) => option.name}
                onSelect={handleTargetProposalSelect}
                data-testid='target-activities-autocomplete'
                placeholder={t('proposalTargetActivity')}
                value={selectedTargetProposal}
                inputValue={targetLabelAutocomplete}
                onInputChange={(value) => setTargetLabelAutocomplete(value)}
              />
            </div>
          </div>
          {(selectedSourceProposal && (activities.find(a => a.relationId === selectedSourceProposal?.id)?.new || !activities.find(a => a.relationId === selectedSourceProposal?.id)?.ruleMatch)) &&
            <div style={{ marginTop: '20px' }}>
              <ButtonFileUploader
                id='upload-programFile'
                defaultFile={activities.find(a => a.relationId === selectedSourceProposal?.id)?.program_file ?? null}
                documentAllowedSize={programFileSize}
                documentType='PROGRAM'
                inputFileDocumentName={activities.find(a => a.relationId === selectedSourceProposal?.id)?.name ?? ''}
                label={activities.find(a => a.relationId === selectedSourceProposal?.id)?.program_file?.file_name ?? t('requiresProgram')}
                onFileUploded={setFile}
                validation={academictranscriptRule.adornment}
                gridDirectionRow={true}
                buttonStyle={{ marginRight: '10px' }}
              />
              <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '15px' }}>
                {t('minContentTargetSelectionDescripcion')}
              </Typography>
              <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '15px' }}>
                {t('sourceProposalProgramCondition')}
              </Typography>
              <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '15px' }}>
                {t('sourceProposalProgramValidation')}
              </Typography>
            </div>
          }
          <div>
            {minContent && minContent?.length > 0 &&
              <Box sx={{ paddingTop: '20px' }}>
                <Typography variant='body1'>{t('minContent')}</Typography>
                <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '10px', marginBottom: '10px' }}>
                  {t('minContentDescription')}
                </Typography>
                <List sx={{ overflowY: 'auto', maxHeight: '400px' }} disablePadding>
                  {minContent.map((item: minContentSelected) => {
                    return (
                      <ListItem key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px', alignItems: 'flex-start' }} >
                        <Typography variant='body1'>{item.description}</Typography><Checkbox sx={{ padding: 0, marginLeft: '10px' }} checked={item.status} onChange={() => handleCheckMinContent(item.id, !item.status)} />
                      </ListItem>
                    )
                  })}
                </List>
              </Box>
            }
            {!selectedTargetProposal && <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '20px' }}>
              {t('minContentHelpText')}
            </Typography>
            }
            {selectedTargetProposal && minContent && minContent?.length <= 0 && <Typography color="#949494" sx={{ fontSize: '14px', marginTop: '20px' }}>
              {t('minContentDescripcion')}
            </Typography>
            }
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => selectedSourceProposal ? saveIncompleteProposal(selectedSourceProposal.id) : handleClose()} color="primary" variant='outlined' data-testid='close-proposal-button'>
            {t('closeLabel')}
          </Button>
          <Button onClick={() => handleAddProposal()} color="primary" variant='contained' disabled={isDisabledAddProposal} data-testid='add-proposal-button'>
            {isProposalEditionId ? t('editLabelCapitalize') : t('Add')}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={openUncheckActivityDialog}
        message={t('uncheckActivityDialogMessageOne') + activityToUncheck?.name}
        title={t('caution')}
        cancelLabel={t('cancelLabel')}
        confirmLabel={t('confirm')}
        onClose={() => setOpenUncheckActivityDialog(false)}
        onConfirm={() => handleConfirmUncheckActivity()}
        showCustomBody={true}
        renderCustomBody={<Typography sx={{ marginTop: '10px' }}>{t('uncheckActivityDialogMessageTwo')}</Typography>}
      >
      </ConfirmDialog>


    </>
  );
};
export default TargetActivitiesTable;
